import { useState } from 'react';
import { Button, Flex, Modal, Select, Stack, useTheme } from '@fti/design-system';
import { useStartExecution } from '../../../api/media-sim/executions';

interface StartExecutionButtonProps {
  projectId: string;
  runId: string;
  disabled?: boolean;
  onExecutionCreated?: (executionId: string) => void;
  /** Lock the execution type — hides the type selector. */
  fixedType?: 'sample' | 'full';
  /** Override the button label. */
  label?: string;
  /** When set, clicking opens a confirm dialog before starting (e.g. re-running
   *  a sample destroys the previous sample's results). */
  confirm?: { title: string; message: string; confirmLabel: string };
}

const EXECUTION_OPTIONS = [
  { value: 'sample', label: 'Sample' },
  { value: 'full', label: 'Full Simulation' },
];

export function StartExecutionButton({
  projectId,
  runId,
  disabled,
  onExecutionCreated,
  fixedType,
  label,
  confirm,
}: StartExecutionButtonProps) {
  const { theme } = useTheme();
  const { startExecution, loading } = useStartExecution();
  const [executionType, setExecutionType] = useState<'sample' | 'full'>(fixedType ?? 'sample');
  const [errorModal, setErrorModal] = useState<{ open: boolean; errors: string[] }>({
    open: false,
    errors: [],
  });
  const [confirmOpen, setConfirmOpen] = useState(false);

  const startExecutionNow = async () => {
    try {
      const execution = await startExecution(projectId, runId, executionType);
      if (execution?.id) {
        onExecutionCreated?.(execution.id);
      }
    } catch (err: unknown) {
      const apiErr = err as { status?: number; message?: string };
      if (apiErr?.status === 400) {
        setErrorModal({ open: true, errors: [apiErr.message || 'Validation failed'] });
      } else if (apiErr?.status === 409) {
        setErrorModal({ open: true, errors: ['An execution of this type already exists for this run.'] });
      }
    }
  };

  // Gate behind the confirm dialog when configured; otherwise start immediately.
  const handleStart = () => {
    if (confirm) setConfirmOpen(true);
    else startExecutionNow();
  };

  const handleConfirm = async () => {
    setConfirmOpen(false);
    await startExecutionNow();
  };

  return (
    <>
      <Flex align="end" gap="sm">
        {!fixedType && (
          <div style={{ minWidth: 160 }}>
            <Select
              label="Execution Type"
              options={EXECUTION_OPTIONS}
              value={executionType}
              onChange={(value) => setExecutionType(value as 'sample' | 'full')}
            />
          </div>
        )}
        <Button
          variant="solid"
          color="primary"
          onClick={handleStart}
          loading={loading}
          disabled={disabled || loading}
        >
          {label ?? `Start ${executionType === 'sample' ? 'Sample' : 'Full Simulation'}`}
        </Button>
      </Flex>

      <Modal isOpen={errorModal.open} onClose={() => setErrorModal({ open: false, errors: [] })}>
        <Modal.Header>
          <h2 style={{ margin: 0, color: theme.colors.text.primary }}>Cannot Start Execution</h2>
        </Modal.Header>
        <Modal.Body>
          <Stack gap="sm">
            {errorModal.errors.map((err, i) => (
              <p key={i} style={{ color: theme.colors.semantic.error.default, margin: 0 }}>
                {err}
              </p>
            ))}
          </Stack>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="ghost" onClick={() => setErrorModal({ open: false, errors: [] })}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {confirm && (
        <Modal isOpen={confirmOpen} onClose={() => setConfirmOpen(false)}>
          <Modal.Header>
            <h2 style={{ margin: 0, color: theme.colors.text.primary }}>{confirm.title}</h2>
          </Modal.Header>
          <Modal.Body>
            <p style={{ margin: 0, color: theme.colors.text.primary }}>{confirm.message}</p>
          </Modal.Body>
          <Modal.Footer>
            <Flex gap="sm" justify="end">
              <Button variant="ghost" onClick={() => setConfirmOpen(false)}>Cancel</Button>
              <Button variant="solid" color="error" onClick={handleConfirm} loading={loading}>
                {confirm.confirmLabel}
              </Button>
            </Flex>
          </Modal.Footer>
        </Modal>
      )}
    </>
  );
}
